<script setup lang="ts">
import { CupraButton } from '@/components/cupra/button'
import { CupraInput } from '@/components/cupra/input'
import { computed, getCurrentInstance, ref } from 'vue'

const props = defineProps<{
    withoutPhone?: boolean
    withoutLicensePlate?: boolean
    submitLabel?: string
}>()
const emit = defineEmits(['back', 'submit'])

const hasBackListener = computed(() => !!getCurrentInstance()?.vnode.props?.['onBack'])

interface FormErrors {
    firstName?: string
    lastName?: string
    email?: string
    phone?: string
    licensePlate?: string
}

const firstName = ref<string>()
const lastName = ref<string>()
const email = ref<string>()
const phone = ref<string>()
const licensePlate = ref<string>()

const formErrors = ref<FormErrors>({})

const isSubmitPossible = computed(() => firstName.value && lastName.value && email.value && (props.withoutLicensePlate || licensePlate.value))
const isValidEmail = computed(() => {
    if (!email.value) {
        return false
    }
    return (
        email.value
            .toLowerCase()
            .match(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            ) !== null
    )
})

const submitIfPossible = () => {
    formErrors.value = {}

    if (!firstName.value) {
        formErrors.value.firstName = 'Vul je voornaam in'
    }

    if (!lastName.value) {
        formErrors.value.lastName = 'Vul je achternaam in'
    }

    phone.value = phone.value?.trim() || undefined
    if (phone.value && !phone.value.match(/0[0-9]{9}/)) {
        formErrors.value.phone = 'Vul een geldig Nederlands (of geen) telefoonnummer in'
    }

    if (!email.value) {
        formErrors.value.email = 'Vul je e-mailadres in'
    } else if (!isValidEmail.value) {
        formErrors.value.email = 'Vul een geldig e-mailadres in'
    }

    if (!props.withoutLicensePlate) {
        if (!licensePlate.value) {
            formErrors.value.licensePlate = 'Vul je kenteken in'
        } else if (licensePlate.value.length !== 6) {
            formErrors.value.licensePlate = 'Vul een geldig kenteken in'
        }
    }

    if (Object.keys(formErrors.value).length > 0) {
        return
    }

    emit('submit', firstName.value, lastName.value, email.value, phone.value, licensePlate.value)
}
</script>

<template>
    <div
        v-if="$slots.default"
        class="mt-1.5 mb-3"
    >
        <slot />
    </div>
    <form @submit.prevent="submitIfPossible()">
        <div class="my-6 grid gap-4 sm:grid-cols-2">
            <CupraInput
                type="text"
                placeholder="Voornaam"
                v-model="firstName"
                :error="formErrors.firstName"
            />
            <CupraInput
                type="text"
                placeholder="Achternaam"
                v-model="lastName"
                :error="formErrors.lastName"
            />
            <CupraInput
                type="text"
                placeholder="E-mailadres"
                v-model="email"
                :error="formErrors.email"
            />
            <CupraInput
                v-if="!withoutPhone"
                type="tel"
                placeholder="Bel mij op (optioneel)"
                maxlength="10"
                pattern="0[0-9]{9}"
                v-model="phone"
                :error="formErrors.phone"
            />
            <CupraInput
                v-if="!withoutLicensePlate"
                type="text"
                placeholder="Kenteken (ABC12A)"
                v-model="licensePlate"
                maxlength="6"
                :error="formErrors.licensePlate"
            />
        </div>
        <div class="mt-6 grid gap-2 sm:grid-cols-2">
            <CupraButton
                type="submit"
                :disabled="!isSubmitPossible"
                >{{ submitLabel || 'Aanvragen' }}</CupraButton
            >
            <CupraButton
                v-if="hasBackListener"
                class="sm:order-first"
                variant="secondary"
                @click="$emit('back')"
                direction="back"
                >Terug</CupraButton
            >
        </div>
    </form>
</template>
